import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { AnyAction } from 'redux';
import axios from 'axios/axios-base';
import * as actionTypes from 'constants/action-types';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { action } from 'typesafe-actions';
import {
  createServiceFromTemplateThunk,
  getMockServiceListThunk,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { applySearchTermFilterToList } from 'utils/filterUtils';
import MixpanelService from 'utils/mixpanelService';
import {
  CREATE_BLANK_TEMPLATE,
  CREATE_SERVICE_FROM_TEMPLATE,
  DELETE_TEMPLATE,
  UPDATE_TEMPLATE,
} from 'utils/mixpanelEvents';
import { getDefaultServiceId } from 'store/ui/service/serviceActions';
import i18n from 'i18n';
import { generateDate } from 'utils/dateUtil';
import { MockServiceTemplate } from './mockServiceTemplateTypes';
import { UiState } from 'store/ui/uiReducer';
import { MockServiceTemplatesState } from './mockServiceTemplateReducer';
import _ from 'lodash';
import { getDifference } from 'utils/miscUtil';
import { AssetAssignment, MockService } from '../../types/rest/mockServiceTypes';
import store from '../configureStore';
import { sanitizeMockPatchDto } from '../../utils/mockServiceUtils';
import acApi from '../../axios/ac/acApi';
import { fetchTransactionsForTemplate } from './mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { Filter } from '../mockServices/mockServiceTypes';

const templatesUrl = `/workspaces/{workspaceId}/service-mock-templates`;

/**
 * Creates blank virtual services template action.
 */
export const createBlankMockServiceTemplateAction = (mockServiceTemplate: MockServiceTemplate) =>
  action(actionTypes.MOCK_SERVICE_TEMPLATES_CREATE_BLANK, mockServiceTemplate);

/**
 * Fetch of the templates started.
 */
export const showLoadingIndicator = () => action(actionTypes.MOCK_SERVICE_TEMPLATES_SHOW_LOADING_INDICATOR, {});

/**
 * Enables / Disables isSaving state for the list.
 */
export const setIsSaving = (isSaving: boolean) => action(actionTypes.MOCK_TEMPLATES_SET_SAVING_STATE, isSaving);

/**
 * Update Page Filter.
 * @param filter
 */
export const updateFilter = (filter: Filter) => action(actionTypes.MOCK_SERVICE_TEMPLATES_UPDATE_FILTER, filter);

/**
 * Update single template - redux action.
 * @param template
 */
export const updateMockServiceTemplateAction = (template: MockServiceTemplate) =>
  action(actionTypes.MOCK_SERVICE_TEMPLATES_UPDATE, template);

/**
 * Collapses the templates.
 */
export const collapseAllTemplates = () => action(actionTypes.MOCK_SERVICE_TEMPLATES_COLLAPSE_ALL);

/**
 * Swaps original / draft of the template.
 * @param id
 * @param template
 */
export const swapMockServiceTemplateAction = (id: number, template: MockServiceTemplate) =>
  action(actionTypes.SWAP_MOCK_SERVICE_TEMPLATE_DRAFT, { id: id, template: template });

/**
 * Update single template - redux action.
 * @param templateId
 */
export const expandTemplate = (templateId: number | null) =>
  action(actionTypes.MOCK_SERVICE_TEMPLATES_EXPAND_ITEM, templateId);

/**
 * Creates virtual service template.
 * @param template
 */
export const createTemplateDraft = (template: MockServiceTemplate): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: any) => {
    try {
      return (await axios.post(`/workspaces/{workspaceId}/service-mock-template-drafts?templateId=${template.id}`)).data
        .result;
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          'Could not create Virtual Service Template Draft',
          error.toLocaleString(),
        ),
      );
    }
  };
};

/**
 *  Update of the template. Creates a draft version of template if we are working with "original" template version.
 */
export const updateMockServiceTemplate = (template: MockServiceTemplate): any => {
  return async (dispatch: any, getState: any) => {
    //Expand template if it is not already
    const expandedItemId = getState().mockServiceTemplates.expandedTemplateId;
    if (expandedItemId !== template.id) {
      dispatch(expandTemplate(template.id));
    }

    dispatch(setIsSaving(true));

    const templatesState = getState().mockServiceTemplates as MockServiceTemplatesState;
    const newTemplate = _.cloneDeep(template);
    const originalTemplate = _.cloneDeep(templatesState.mockServiceTemplates.find(item => item.id === template.id));

    if (!originalTemplate) {
      return;
    }

    if (template.isDraft !== true) {
      const draft = await dispatch(createTemplateDraft(template));
      // If draft creation failed
      if (!draft) {
        return;
      }
      newTemplate.id = draft.id;
      newTemplate.isDraft = true;
      newTemplate.serviceMockTemplateId = template.id;
      await dispatch(swapMockServiceTemplateAction(template.id, newTemplate));
    } else {
      await dispatch(updateMockServiceTemplateAction(template));
    }

    let diff = getDifference(newTemplate, originalTemplate);

    // Sanitize values from a result of the patch diff
    diff = sanitizeMockPatchDto(diff, newTemplate);

    if (!diff) {
      return;
    }

    try {
      await axios.patch(`/workspaces/{workspaceId}/service-mock-template-drafts/${newTemplate.id}`, diff);
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.cannotUpdateTemplate'),
          i18n.t('mockServices:errorMsgs.cantSaveTemplate', {
            reason: error.response ? error.response.data.error : 'Unexpected error occurred.',
          }),
        ),
      );
    } finally {
      //Reload shuttle if priority mode changes
      if (originalTemplate.priorityMode !== newTemplate.priorityMode) {
        const activeRunner = getState().mockServiceTemplateDetail.runnerTab;
        const transactionType = activeRunner === 'httpRunner' ? 'HTTP' : 'MESSAGING';
        await dispatch(fetchTransactionsForTemplate(newTemplate, 'RIGHT', transactionType));
      }
    }

    // Do patch request, compare original with a new version and send a patch
    await dispatch(setIsSaving(false));
  };
};

/**
 * Conditionally refresh the list of virtual services if it contains at lest one virtual service which has
 * the following template applied.
 * @param template
 */
const templateSaveFinished = (template: MockServiceTemplate) => {
  // If there is any virtual service which uses this template, refresh the list of templates
  const mockServices: MockService[] = (store.getState() as ApplicationState).mockservices.dataList;
  const index = _.findIndex(
    mockServices,
    (ms: MockService) =>
      ms.appliedTemplateId === template.id || ms.appliedTemplateId === template.serviceMockTemplateId,
  );
  if (index !== -1) {
    store.dispatch(getMockServiceListThunk() as any);
  }
};

/** Creates new template. */
export const createMockServiceTemplate = (template: any, serviceId: number) => {
  return axios.post(`${templatesUrl}?serviceId=${serviceId}`, template);
};

/**
 * Creates new virtual service template draft.
 * @param name
 * @param serviceId
 */
export const createMockServiceTemplateDraft = (name: string, serviceId: number) => {
  return axios.post(`workspaces/{workspaceId}/service-mock-template-drafts`, {
    serviceId: serviceId,
    name: name,
  });
};

/**
 * Gets currently expanded template.
 */
export const getExpandedTemplate = () => {
  const expandedTemplateId = (store.getState() as ApplicationState).mockServiceTemplates.expandedTemplateId;
  if (_.isNil(expandedTemplateId)) {
    return null;
  }
  return (store.getState() as ApplicationState).mockServiceTemplates.mockServiceTemplates.find(
    tmpl => expandedTemplateId === tmpl.id,
  );
};

/**
 * Create blank template with preselected Service.
 */
export const createBlankMockServiceTemplate = (): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>): Promise<MockServiceTemplate> => {
    MixpanelService.track(CREATE_BLANK_TEMPLATE());

    return new Promise<MockServiceTemplate>(async (resolve, reject) => {
      //Preselect Service, by default
      const selectedId = await dispatch(getDefaultServiceId(true));
      const templateName = 'MockServiceTemplate-' + generateDate();

      try {
        const savedTemplate: MockServiceTemplate = (await createMockServiceTemplateDraft(templateName, selectedId)).data
          .result;

        // Backend does not return isDraft attribute
        savedTemplate.isDraft = true;

        // Do the change in the redux
        dispatch(createBlankMockServiceTemplateAction(savedTemplate));

        // Refresh list of services
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('mockServices:successMsgs.createSuccessMsgTempl'),
            i18n.t('mockServices:successMsgs.templateCreated', {
              name: savedTemplate.name,
            }),
          ),
        );

        //Resolve promise with template
        resolve(savedTemplate);
      } catch (error) {
        // TODO - Show error toast in case of error
        console.log('Cannot create template', error);
        reject();
      }
    });
  };
};

/**
 * Gets assets for a virtual service and remove any server configuration if necessary (for example when MS is on a location
 * which does not support it (public one))
 * @param smTemplate - template to get assets from
 * @param smTemplate - if true, assets for ssl server configuration will not be included
 * @param omitServerAssets - if true only client assets will be included.
 */
export const getAssetsForMockServiceFromTemplate = async (
  smTemplate: MockServiceTemplate,
  omitServerAssets: boolean,
) => {
  let assets: AssetAssignment[] = [];
  let configKeyAssets: AssetAssignment[] = [];
  let mockServiceTransactions: [] = [];
  let mockServiceRecordings: [] = [];
  try {
    const currentTemplate = smTemplate.isDraft
      ? await acApi.templateDrafts.getById(smTemplate.serviceMockTemplateId)
      : await acApi.templates.getById(smTemplate.id);

    const data = currentTemplate.data;
    assets = _.get(data, 'result.assets');
    configKeyAssets = _.get(data, 'result.configKeyAssets');
    mockServiceTransactions = _.get(data, 'result.mockServiceTransactions');
    mockServiceRecordings = _.get(data, 'result.mockServiceRecordings');
  } catch (e) {
    store.dispatch(showToastNotification(ToastType.Warning, 'Cannot assign Certificates to the new Virtual Service.'));
  }

  if (omitServerAssets) {
    assets = _.filter(assets, asset => _.startsWith(asset.assetUsageType, 'CLIENT_'));
    configKeyAssets = _.filter(assets, asset => _.startsWith(asset.assetUsageType, 'CLIENT_'));
  }
  return {
    assets: assets,
    configKeyAssets: configKeyAssets,
    mockServiceTransactions: mockServiceTransactions,
    mockServiceRecordings: mockServiceRecordings,
  };
};

/**
 * Creates a new Virtual Service object from the passed Virtual Service Template
 * @param smTemplate
 */
export const createMockServiceFromTemplate = (
  smTemplate: MockServiceTemplate,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<any, null, AnyAction>, getState: any) => {
    MixpanelService.track(CREATE_SERVICE_FROM_TEMPLATE());

    //Save the template if it is a draft
    if (smTemplate.isDraft) {
      await dispatch(saveMockServiceTemplate(smTemplate, false));
    }

    //Once saved, start creation of the service
    dispatch({
      type: actionTypes.MOCK_SERVICE_TEMPLATES_SERVICE_CREATION_STARTED,
      payload: smTemplate.id,
    });

    //Get assets for this template
    const assetBundle = await getAssetsForMockServiceFromTemplate(smTemplate, true);
    smTemplate = {
      ...smTemplate,
      assets: assetBundle.assets,
      configKeyAssets: assetBundle.configKeyAssets,
      mockServiceTransactions: assetBundle.mockServiceTransactions,
      mockServiceRecordings: assetBundle.mockServiceRecordings,
    };

    dispatch(createServiceFromTemplateThunk(smTemplate))
      .then(() => {
        //If there is something in page filter search, refresh the list of services
        if (getState().ui.pageFilter.searchTerm !== '') {
          dispatch(getMockServiceListThunk());
        }
      })
      .catch((error: any) => {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('errorMsgs.networkProblem'),
            i18n.t('mockServices:errorMsgs.cantSaveTemplate', {
              reason: error.response ? error.response.data.error : 'Something went wrong.',
            }),
          ),
        );
      })
      .finally(() => {
        // Service creation finished
        dispatch({
          type: actionTypes.MOCK_SERVICE_TEMPLATES_SERVICE_CREATION_FINISHED,
          payload: smTemplate.id,
        });
      });
  };
};

/**
 * Initiates deletion of the virtual service template.
 * @param template
 */
export const deleteMockServiceTemplate = (
  template: MockServiceTemplate,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const templateId = template.id;
    let deleteUrl = `/workspaces/{workspaceId}/service-mock-templates/${templateId}`;
    if (template.isDraft) {
      deleteUrl = `/workspaces/{workspaceId}/service-mock-template-drafts/${templateId}`;
    }

    // For other permanent templates, trigger backend call
    try {
      MixpanelService.track(DELETE_TEMPLATE(false));
      await axios.delete(deleteUrl);
      dispatch({
        type: actionTypes.MOCK_SERVICE_TEMPLATES_DELETE,
        payload: templateId,
      });
      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('mockServices:successMsgs.templateDeletedHeading'),
          i18n.t('mockServices:successMsgs.templateDeletedText'),
        ),
      );
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cantDeleteTemplate'),
        ),
      );
    }
  };
};

/**
 * Bulk deletes virtual service templates and/or drafts.
 * Selected items are partitioned by isDraft flag and both API calls are issued concurrently.
 * A warning toast is shown on partial failure; a danger toast on total failure.
 * @param selectedItems list of selected template rows (mix of templates and drafts)
 */
export const bulkDeleteTemplates = (
  selectedItems: MockServiceTemplate[],
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const templateItems = selectedItems.filter(item => !item.isDraft);
    const draftItems = selectedItems.filter(item => item.isDraft);

    const templateIds = templateItems.map(item => item.id);
    const draftIds = draftItems.map(item => item.id);

    const calls: Promise<any>[] = [];
    if (templateIds.length > 0) {
      calls.push(acApi.templates.bulkDeleteMockServiceTemplates(templateIds));
    }
    if (draftIds.length > 0) {
      calls.push(acApi.templateDrafts.bulkDeleteMockServiceTemplateDrafts(draftIds));
    }

    if (calls.length === 0) {
      return;
    }

    const results = await Promise.allSettled(calls);
    const allFailed = results.every(r => r.status === 'rejected');
    const anyFailed = results.some(r => r.status === 'rejected');

    if (allFailed) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cantDeleteTemplate'),
        ),
      );
    } else if (anyFailed) {
      dispatch(
        showToastNotification(
          ToastType.Warning,
          i18n.t('mockServices:warnMsgs.bulkDeletePartialSuccess'),
          i18n.t('mockServices:warnMsgs.bulkDeleteSomeItemsFailed'),
        ),
      );
    } else {
      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('mockServices:successMsgs.templateDeletedHeading'),
          i18n.t('mockServices:successMsgs.templateDeletedText'),
        ),
      );
    }
    dispatch(getMockServiceTemplates(undefined, true));
  };
};

/**
 * Gets list of Virtual Service Templates / Virtual Service Templates drafts.
 *
 * @param serviceId service identifier
 * @param forceRefresh results are cached from small amount of time, this boolean forces loading new results from backend
 * @param backgroundLoading whether to show loading indicator. If se to true, loader is not shown
 * @param isNew
 * @param templateId
 */
export const getMockServiceTemplates = (
  serviceId?: number,
  forceRefresh?: boolean,
  backgroundLoading?: boolean,
  isNew?: boolean,
  templateId?: number,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    try {
      //Fetch templates only in case they haven't been fetched recently (in 5 minutes)
      if (forceRefresh !== undefined && !forceRefresh) {
        const lastUpdate = getState().services.lastUpdate;
        if (lastUpdate != null && lastUpdate > Date.now() - 5 * 60 * 1000) {
          return;
        }
      }

      // Show loading indicator only if it should not be background loading
      if (!(backgroundLoading !== undefined && backgroundLoading)) {
        dispatch(showLoadingIndicator());
      }

      // Prepare params for axios
      let params: any = {
        limit: '-1',
      };

      // If service filter is enabled, include serviceId to param
      const uiState = getState().ui as UiState;
      const serviceId = uiState.pageFilter.serviceId;
      if (serviceId > 0) {
        params = { ...params, serviceId: serviceId };
      }

      const response = await axios.get(`/workspaces/{workspaceId}/ui/service-mock-templates`, {
        params: params,
      });

      // Filter results by inline filter if it is enabled (Backend filtering is not working right now)
      const pageFilter = uiState.pageFilter;
      const templates: MockServiceTemplate[] = applySearchTermFilterToList(response.data.result, pageFilter.searchTerm);

      if (isNew) {
        templates.map((item: MockServiceTemplate) => {
          if (item.id === templateId) {
            item.isNew = true;
          }
        });
      }

      dispatch({
        type: actionTypes.MOCK_SERVICE_FETCH_TEMPLATES,
        payload: templates,
      });
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cantGetListOfTemplates'),
        ),
      );
      dispatch({
        type: actionTypes.MOCK_SERVICE_FETCH_TEMPLATES,
        payload: [],
      });
    }
  };
};

//TODO - move to axios package
/**
 * Get virtual service template by id
 * @param id
 */
export const getMockServiceTemplate = (id: number) => {
  return axios.get(`/workspaces/{workspaceId}/service-mock-templates/${id}`);
};

/**
 * Save virtual service template to the backend. In case of new object, update of template results is dispatched.
 * Update does not trigger loading of new templates.
 * @param mockServiceTemplate template to be saved.
 * @param showMessage
 */
export const saveMockServiceTemplate = (
  mockServiceTemplate: MockServiceTemplate,
  showMessage = true,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>, getState: any) => {
    //Do a copy, of template, do not modify reference in redux
    const msTemplateDto = { ...mockServiceTemplate };
    const uiState = getState().ui as UiState;
    const serviceId = uiState.pageFilter.serviceId;

    try {
      dispatch(setIsSaving(true));

      //Update existing template
      MixpanelService.track(UPDATE_TEMPLATE());

      // Save draft
      const savedTemplate = (
        await axios.post(`/workspaces/{workspaceId}/service-mock-template-drafts/${mockServiceTemplate.id}/merge`)
      ).data.result;

      savedTemplate.isDraft = false;
      await dispatch(swapMockServiceTemplateAction(mockServiceTemplate.id, savedTemplate));

      // After update is finished, refresh virtual services if necessary
      templateSaveFinished(mockServiceTemplate);

      if (showMessage) {
        //If user selects service from main service filter then we need to compare with already
        //selected service from existing mock template and if those not equal show the below
        //warning message
        if (serviceId !== 0 && serviceId !== msTemplateDto.serviceId) {
          dispatch(getMockServiceTemplates(serviceId));
          dispatch(
            showToastNotification(
              ToastType.Warning,
              i18n.t('mockServices:successMsgs.templateUpdateSuccessHeading'),
              i18n.t('mockServices:warnMsgs.createMockServiceButFilteredOutTemplate', { name: msTemplateDto.name }),
            ),
          );
        } else {
          dispatch(
            showToastNotification(
              ToastType.Success,
              i18n.t('mockServices:successMsgs.templateUpdateSuccessHeading'),
              i18n.t('mockServices:successMsgs.templateUpdateSuccessText', {
                name: msTemplateDto.name,
              }),
            ),
          );
        }
      }
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cantSaveTemplate', {
            reason: error.response ? error.response.data.error : 'Something went wrong.',
          }),
        ),
      );
    }

    dispatch(setIsSaving(false));
  };
};
